import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { EnvironmentProviders, inject, Provider, provideAppInitializer } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { AuthInterceptorService } from '@portal-core/auth/interceptors/auth-interceptor.service';
import { AuthApiService } from '@portal-core/auth/services/auth-api.service';
import { ProjectHydratorEffect } from '@portal-core/projects/effects/project-hydrator.effect';
import { SiteListByBuildEffect } from '@portal-core/sites/effects/site-list-by-build.effect';
import { WINDOW_PROVIDERS } from '@portal-core/util/window.provider';
import { AuthSiteApiService } from '@portal-output/core/auth/auth-site-api.service';
import { CookieService } from 'ngx-cookie-service';

/**
 * App-wide root providers for portal-output (formerly CoreModule, consumed via importProvidersFrom).
 * Spread into the bootstrapApplication providers in main.ts. NGXS store/state and auth/current service
 * configuration live in main.ts directly; this carries the non-NGXS app-wide providers.
 */
export function provideCore(): (Provider | EnvironmentProviders)[] {
  return [
    CookieService,
    ...WINDOW_PROVIDERS,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptorService, multi: true },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } },
    // Material's MDC mat-select renders a checkmark on the selected option by default; the pre-MDC
    // (Material 15) select hid it. Restore that globally so single-selection dropdowns show no check
    // indicator (multi-select, which uses checkboxes, is unaffected).
    { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
    {
      // Material 21 removed MAT_TOOLTIP_DEFAULT_OPTIONS_FACTORY, so inline the base MatTooltipDefaultOptions
      // defaults it used to return before applying our disableTooltipInteractivity override.
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useFactory: function () {
        return {
          showDelay: 0,
          hideDelay: 0,
          touchendHideDelay: 1500,
          touchGestures: 'auto',
          position: 'below',
          positionAtOrigin: false,
          disableTooltipInteractivity: true
        };
      }
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: {
        panelClass: 'mc-snack-bar'
      }
    },
    { provide: AuthApiService, useClass: AuthSiteApiService },
    provideAppInitializer(() => {
      // Eagerly instantiate these effects at startup so their subscriptions are wired up
      // (previously an APP_INITIALIZER no-op factory with a deps array).
      inject(ProjectHydratorEffect);
      inject(SiteListByBuildEffect);
    })
  ];
}
